import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import Paywall from '../components/Paywall';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const settings = [
  { id: 'notifications', label: 'Push Notifications', sub: 'Daily VIVI Drop & reminders', toggle: true, default: true },
  { id: 'voice_auto', label: 'Auto-play VIVI Voice', sub: 'Hear responses automatically', toggle: true, default: false },
  { id: 'stealth', label: 'Stealth Mode', sub: 'App appears as a wellness tracker', toggle: true, default: false },
  { id: 'biometric', label: 'Biometric Lock', sub: 'Face ID / Fingerprint to open', toggle: true, default: false },
];

export default function Profile() {
  const { user, logout, isPremium, authHeader } = useAuth();
  const navigate = useNavigate();
  const [paywall, setPaywall] = useState(false);
  const [toggles, setToggles] = useState(() => {
    const saved = JSON.parse(localStorage.getItem('intiviv_settings') || '{}');
    return settings.reduce((acc, s) => ({ ...acc, [s.id]: saved[s.id] ?? s.default }), {});
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [journal, setJournal] = useState([]);
  const [journalLoading, setJournalLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/journal`, { headers: authHeader() })
      .then(r => setJournal(r.data.entries || []))
      .catch(() => {})
      .finally(() => setJournalLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeJournal = async (id) => {
    try {
      await axios.delete(`${API}/api/journal/${id}`, { headers: authHeader() });
      setJournal(prev => prev.filter(j => j.id !== id));
      toast.success('Removed from Journal.');
    } catch { toast.error('Could not remove. Please try again.'); }
  };

  const setToggle = (id) => {
    setToggles(prev => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem('intiviv_settings', JSON.stringify(next));
      if (id === 'stealth') {
        toast.info(next.stealth ? 'Stealth mode active — app appears as a wellness tracker.' : 'Stealth mode off.');
      }
      return next;
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const deleteAccount = async () => {
    try {
      toast.success('Account deletion requested. Our team will process this within 24 hours.');
      setShowDeleteConfirm(false);
    } catch { toast.error('Could not process request.'); }
  };

  const firstName = user?.name?.split(' ')[0] || '';
  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  return (
    <div style={{ minHeight: '100vh', paddingBottom: 'var(--bottom-h)' }}>
      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-avatar-lg">{initials}</div>
        <div className="profile-name">{user?.name}</div>
        <div style={{ fontSize: '0.84rem', color: 'var(--text-dim)' }}>{user?.email}</div>
        {isPremium ? (
          <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', background: 'var(--gold-dim)', border: '1px solid var(--border)', fontSize: '0.73rem', fontWeight: 700, letterSpacing: '0.15em', color: 'var(--gold)' }}>
            <i className="fa-solid fa-check" /> EARLY ACCESS MEMBER
          </div>
        ) : (
          <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', background: 'transparent', border: '1px solid var(--border)', fontSize: '0.73rem', fontWeight: 700, letterSpacing: '0.15em', color: 'var(--gold)' }}>
            <i className="fa-solid fa-check" /> EARLY ACCESS MEMBER
          </div>
        )}
      </div>

      {/* Quick stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, margin: '1px 0', background: 'var(--border-soft)' }}>
        {[['Assessments', '16'], ['VIVI Sessions', '∞'], ['Journal', String(journal.length)]].map(([l, v]) => (
          <div key={l} style={{ background: 'var(--bg)', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-ui)', fontSize: '1.32rem', fontWeight: 700, color: 'var(--gold)', marginBottom: 2 }}>{v}</div>
            <div style={{ fontSize: '0.71rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Saved Wisdom (Journal) */}
      <div style={{ marginTop: 8 }} data-testid="saved-wisdom-section">
        <div className="eyebrow" style={{ padding: '16px 20px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span><i className="fa-solid fa-bookmark" style={{ color: 'var(--gold)', marginRight: 8 }} />Saved Wisdom</span>
          {!journalLoading && journal.length > 0 && (
            <span style={{ fontSize: '0.71rem', color: 'var(--text-dim)', letterSpacing: '0.1em' }}>{journal.length} saved</span>
          )}
        </div>
        {journalLoading ? (
          <div style={{ padding: '14px 20px', fontSize: '0.83rem', color: 'var(--text-dim)' }}>Loading…</div>
        ) : journal.length === 0 ? (
          <div style={{ padding: '8px 20px 18px', fontSize: '0.87rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Tap the <i className="fa-regular fa-bookmark" style={{ color: 'var(--gold)', margin: '0 4px' }} /> icon under any VIVI reply to keep it here for later.
          </div>
        ) : (
          <div style={{ padding: '0 16px 6px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {journal.slice(0, 8).map(j => (
              <div key={j.id} className="immersive-card" style={{ padding: '14px 14px 12px', position: 'relative' }} data-testid={`journal-${j.id}`}>
                <div style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif", fontStyle: 'italic', fontSize: '1.01rem', lineHeight: 1.55, color: 'var(--text)', paddingRight: 28 }}>
                  "{(j.content || '').replace(/^#{1,6}\s+/gm, '').replace(/\*\*(.+?)\*\*/g, '$1').slice(0, 220)}{(j.content || '').length > 220 ? '…' : ''}"
                </div>
                <div style={{ marginTop: 8, fontSize: '0.71rem', color: 'var(--text-dim)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  {j.created_at ? new Date(j.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                </div>
                <button
                  onClick={() => removeJournal(j.id)}
                  title="Remove"
                  data-testid={`journal-delete-${j.id}`}
                  style={{
                    position: 'absolute', top: 10, right: 10,
                    background: 'none', border: 'none', color: 'var(--text-dim)',
                    cursor: 'pointer', fontSize: '0.79rem', padding: 4,
                  }}
                >
                  <i className="fa-regular fa-trash-can" />
                </button>
              </div>
            ))}
            {journal.length > 8 && (
              <div style={{ padding: '6px 4px 12px', fontSize: '0.76rem', color: 'var(--text-dim)', letterSpacing: '0.1em', textAlign: 'center' }}>
                + {journal.length - 8} more saved
              </div>
            )}
          </div>
        )}
      </div>

      {/* Settings */}
      <div style={{ marginTop: 8 }}>
        <div className="eyebrow" style={{ padding: '16px 20px 8px' }}>Settings</div>
        {settings.map(s => (
          <div key={s.id} className="setting-row" onClick={() => setToggle(s.id)}>
            <div>
              <div className="setting-label">{s.label}</div>
              <div className="setting-sub">{s.sub}</div>
            </div>
            <div className={`toggle${toggles[s.id] ? ' on' : ''}`}>
              <div className="toggle-thumb" />
            </div>
          </div>
        ))}
      </div>

      {/* Links */}
      <div style={{ marginTop: 8 }}>
        <div className="eyebrow" style={{ padding: '16px 20px 8px' }}>Account</div>
        {[
          { icon: 'fa-clipboard-check', label: 'My Assessments', action: () => navigate('/assessments') },
          { icon: 'fa-calendar-check', label: 'My Consultations', action: () => navigate('/consult') },
          { icon: 'fa-user-doctor', label: 'Meet the Founder', action: () => navigate('/founder') },
          { icon: 'fa-crown', label: 'Subscription & Billing', action: () => setPaywall(true) },
          { icon: 'fa-shield-halved', label: 'Privacy Policy', action: () => toast.info('Privacy policy — coming soon.') },
          { icon: 'fa-file-lines', label: 'Terms of Service', action: () => toast.info('Terms — coming soon.') },
          { icon: 'fa-circle-question', label: 'Help & Support', action: () => toast.info('Email: intiviv@gmail.com') },
        ].map(row => (
          <div key={row.label} className="setting-row" onClick={row.action}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <i className={`fa-solid ${row.icon}`} style={{ color: 'var(--gold)', width: 18, textAlign: 'center' }} />
              <div className="setting-label">{row.label}</div>
            </div>
            <i className="fa-solid fa-chevron-right" style={{ color: 'var(--text-dim)', fontSize: '0.79rem' }} />
          </div>
        ))}
      </div>

      {/* Danger zone */}
      <div style={{ margin: '20px 16px', padding: '16px', border: '1px solid rgba(239,68,68,0.15)', background: 'rgba(239,68,68,0.03)' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.15em', color: '#ef4444', marginBottom: 12 }}>DANGER ZONE</div>
        <button onClick={() => setShowDeleteConfirm(true)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', color: '#ef4444', fontFamily: 'var(--font-ui)' }}>
          <i className="fa-solid fa-trash" style={{ marginRight: 8 }} />Delete My Account & All Data
        </button>
      </div>

      <div style={{ padding: '0 16px 24px' }}>
        <button className="btn-outline w-full" onClick={handleLogout} data-testid="logout-btn">
          <i className="fa-solid fa-right-from-bracket" /> SIGN OUT
        </button>
      </div>

      <div style={{ textAlign: 'center', padding: '0 0 16px', fontSize: '0.71rem', color: 'var(--text-dim)', letterSpacing: '0.15em' }}>
        INTIVIV™ v1.0 · © 2026 The Yellow Tribe
      </div>

      {/* Delete confirm */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setShowDeleteConfirm(false)}>
            <motion.div style={{ width: '100%', maxWidth: 400, margin: 'auto', background: 'var(--bg2)', border: '1px solid rgba(239,68,68,0.3)', padding: 28 }}
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: '2.5rem', color: '#ef4444', display: 'block', marginBottom: 12 }} />
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', marginBottom: 8 }}>Delete Account?</div>
                <p style={{ fontSize: '0.89rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  This will permanently delete your account, all VIVI conversations, assessments, and personal data. This cannot be undone.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn-outline w-full" onClick={() => setShowDeleteConfirm(false)}>CANCEL</button>
                <button style={{ flex: 1, padding: '13px', background: '#ef4444', color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.81rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'var(--font-ui)' }} onClick={deleteAccount}>
                  DELETE ALL
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {paywall && <Paywall onClose={() => setPaywall(false)} />}

      {/* Legal / HIPAA-style disclaimers */}
      <div className="legal-disclaimer" data-testid="legal-disclaimer" style={{ marginTop: 16 }}>
        <p style={{ marginBottom: 6 }}><strong>Wellness, not medical advice.</strong> INTIVIV™ provides general intimate wellness information and AI-assisted educational guidance via VIVI. The content is not a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of a qualified clinician with any questions regarding a medical condition.</p>
        <p style={{ marginBottom: 6 }}><strong>No doctor-patient relationship.</strong> Use of VIVI, the assessments, the education library, or any other feature does not create a doctor-patient relationship with Dr. Vikram Dukle, ANAMIVA Clinic, Dr. Dukle's Hospital &amp; Research Centre, or INTIVIV. Such a relationship is established only through a formal consultation booked separately.</p>
        <p style={{ marginBottom: 6 }}><strong>Privacy &amp; data protection.</strong> All conversations and personal data are encrypted in transit (TLS 1.2+) and at rest. We do not sell or share personal health information. We comply with applicable Indian DPDP Act, EU GDPR and US HIPAA-aligned safeguards where relevant. You may delete your conversations and account at any time.</p>
        <p style={{ marginBottom: 6 }}><strong>Emergency notice.</strong> If you are experiencing a medical emergency — severe pain, heavy bleeding, fever, signs of infection, suicidal thoughts or any condition that may threaten life — please contact local emergency services immediately. INTIVIV is not designed for emergencies.</p>
        <p style={{ marginBottom: 6 }}><strong>Age requirement.</strong> INTIVIV is intended for users aged 18 and above. Adolescents aged 13–17 may use the limited education mode under parental guidance only.</p>
        <p style={{ marginBottom: 0 }}><strong>Product disclaimer.</strong> Product recommendations within VIVI are general wellness suggestions, not medical prescriptions. They are not intended to diagnose, treat, cure or prevent any disease.</p>
      </div>
    </div>
  );
}
